import {ref} from "vue";

/**
 * The "latest run wins" guard shared by the composables that refetch on
 * navigation: a slow earlier response must never overwrite a newer one, and
 * neither may it clear the flags of the run that superseded it.
 *
 * The task decides what to write and when — it receives a predicate telling
 * it whether it is still the latest — while this owns the token and the two
 * status flags. Written by hand in each composable, the pattern drifted: one
 * gained a failure flag before the other.
 */
const useLatestFetch = () => {
    const loading = ref<boolean>(false);
    const failed = ref<boolean>(false);

    let current = 0;

    const run = async (task: (isLatest: () => boolean) => Promise<void>) => {
        const id = ++current;
        const isLatest = () => id === current;

        loading.value = true;

        try {
            await task(isLatest);

            if (isLatest()) {
                failed.value = false;
            }
        } catch {
            // Swallowed on purpose: callers keep whatever they already had and
            // read `failed` to say so, rather than surfacing a rejection into
            // a watcher that cannot handle it.
            if (isLatest()) {
                failed.value = true;
            }
        } finally {
            if (isLatest()) {
                loading.value = false;
            }
        }
    };

    return {loading, failed, run};
};

export default useLatestFetch;
